import React, { useEffect, useRef, useState } from "react";
import { modifyMediaUrl } from "../../helpers/modifyMediaUrl";
import { translate } from "../../localization/translateService";
import { getFlagCode } from "../../services/flag";
import { CurrencyText } from "../common/CurrencyText";
import { getI18nLanguageFromLocalStorage } from "../../helpers/localStorageHelper";
import { useShoppingCartActions } from "../../hooks/useShoppingCartActions";

const VODItem = ({
  course,
  onClick,
  translations,
  toast,
}) => {
  const videoRef = useRef(null);
  const [translatedCategory, setTranslatedCategory] = useState(course.category);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const deleteLoadingRef = useRef(false);
  const lang = getI18nLanguageFromLocalStorage();
  const hoverTimeoutRef = useRef(null);
  const { addToCart, removeFromCart, confirmRemoveFromCart } = useShoppingCartActions({ toast, translations });
  
  useEffect(() => {
    async function loadTranslation() {
      const translated = await translate(course.category);
      setTranslatedCategory(translated);
    }
    loadTranslation();
  }, [course.category]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleRemoveFromCart = async () => {
    setDeleteLoading(true);
    deleteLoadingRef.current = true;
    try {
      await removeFromCart(
        {
          productId: course.productPublicId,
          name: course.title || course.courseName
        },
        {
          setLoading: () => {},
          closeDialog: undefined
        }
      );
    } finally {
      setDeleteLoading(false);
      deleteLoadingRef.current = false;
    }
  };

  const handleConfirmRemove = (e) => {
    if (e) e.stopPropagation();
    
    confirmRemoveFromCart(
      {
        name: course.title || course.courseName,
        productId: course.productPublicId
      },
      {
        onConfirm: handleRemoveFromCart,
        deleteLoadingRef
      }
    );
  };

  const handleCartAction = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Prevent multiple clicks while processing
    if (deleteLoading) {
      return;
    }
    
    if (course?.InCart) {
      // Show confirm dialog before removing
      handleConfirmRemove(e);
    } else {
      // Add to cart directly
      await addToCart(course.productPublicId, 1, { 
        courseTitle: course.title || course.courseName 
      });
    }
  };

  const handleMouseEnter = () => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Set timeout for 1 second before playing video
    hoverTimeoutRef.current = setTimeout(() => {
      if (videoRef.current && course.videoUrl) {
        try {
          videoRef.current.src = modifyMediaUrl(course.videoUrl);
          videoRef.current.load();
          videoRef.current.currentTime = 0;
          
          // Show video with smooth transition
          videoRef.current.style.display = 'block';
          videoRef.current.classList.add('fade-out');
          
          videoRef.current.play().then(() => {
            // Fade in video after it starts playing
            videoRef.current.classList.remove('fade-out');
            videoRef.current.classList.add('fade-in');
          }).catch((err) => {
            console.warn('video autoplay failed:', err);
            videoRef.current.style.display = 'none';
          });
        } catch (error) {
          console.error('error loading video:', error);
        }
      }
    }, 1000);
  };

  const handleMouseLeave = () => {
    // Clear timeout if user leaves before 1 second
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      
      // Fade out video smoothly
      videoRef.current.classList.remove('fade-in');
      videoRef.current.classList.add('fade-out');
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.style.display = 'none';
          videoRef.current.classList.remove('fade-out', 'fade-in');
        }
      }, 300);
    }
  };

  const courseLanguageFlagCode = course.language
    ? getFlagCode(course.language)
    : null;
  const trainerNationalityFlagCode = getFlagCode(course.trainerNationality);

  const priceLabel = course.price != null ? (
      <CurrencyText amount={course.price} sourceCurrency={course.currency} />
    ) : "";

  const originalPriceLabel = (course.originalPrice != null) ? (
      <CurrencyText amount={course.originalPrice} sourceCurrency={course.currency} />
    ) : "";

  const openTrainerProfile = (trainerId, e) => {
    e.stopPropagation();
    window.open(`/${lang}/real-time-lectures/${trainerId}`, '_blank');
  };

  return (
    <div
      className="course-item w-full h-full pb-3"
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="course-item-top" style={{ position: 'relative' }}>
        <div className="tag-container">
          <div className="top-block">
            {courseLanguageFlagCode && (
              <span
                className={`fi fi-${courseLanguageFlagCode?.toLowerCase()} flag`}
              />
            )}
          </div>
          <div className="bottom-block">
            <span className="course-level">{translatedCategory}</span>
          </div>
        </div>

        {!course?.isFree && !course?.isBought && (
          <div
            className={`${course?.InCart ? 'in-cart' : 'add-to-cart'}`}
            onClick={handleCartAction}
          >
            <svg
              alt="Add to card"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.5 5.25H13.5V4.75C13.5 3.55653 13.0259 2.41193 12.182 1.56802C11.3381 0.724106 10.1935 0.25 9 0.25C7.80653 0.25 6.66193 0.724106 5.81802 1.56802C4.97411 2.41193 4.5 3.55653 4.5 4.75V5.25H1.5C1.16848 5.25 0.850537 5.3817 0.616117 5.61612C0.381696 5.85054 0.25 6.16848 0.25 6.5V15C0.25 15.7293 0.539731 16.4288 1.05546 16.9445C1.57118 17.4603 2.27065 17.75 3 17.75H15C15.7293 17.75 16.4288 17.4603 16.9445 16.9445C17.4603 16.4288 17.75 15.7293 17.75 15V6.5C17.75 6.16848 17.6183 5.85054 17.3839 5.61612C17.1495 5.3817 16.8315 5.25 16.5 5.25ZM6 4.75C6 3.95435 6.31607 3.19129 6.87868 2.62868C7.44129 2.06607 8.20435 1.75 9 1.75C9.79565 1.75 10.5587 2.06607 11.1213 2.62868C11.6839 3.19129 12 3.95435 12 4.75V5.25H6V4.75ZM16.25 15C16.25 15.3315 16.1183 15.6495 15.8839 15.8839C15.6495 16.1183 15.3315 16.25 15 16.25H3C2.66848 16.25 2.35054 16.1183 2.11612 15.8839C1.8817 15.6495 1.75 15.3315 1.75 15V6.75H4.5V9C4.5 9.19891 4.57902 9.38968 4.71967 9.53033C4.86032 9.67098 5.05109 9.75 5.25 9.75C5.44891 9.75 5.63968 9.67098 5.78033 9.53033C5.92098 9.38968 6 9.19891 6 9V6.75H12V9C12 9.19891 12.079 9.38968 12.2197 9.53033C12.3603 9.67098 12.5511 9.75 12.75 9.75C12.9489 9.75 13.1397 9.67098 13.2803 9.53033C13.421 9.38968 13.5 9.19891 13.5 9V6.75H16.25V15Z"
                fill="#3054F1"
              />
            </svg>
          </div>
        )}

        {course.thumbnailUrl ? (
          <img
            src={modifyMediaUrl(course.thumbnailUrl)}
            alt={course.courseName}
          />
        ) : (
          <div className="no-thumbnail" />
        )}
        {course.videoUrl && (
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            className="vod-hover-video"
          />
        )}
      </div>
      <div className="course-item-details">
        {course.trainer ? (
          <p className="keduall-typography text-14-semibold-600">
            <a onClick={(e) => openTrainerProfile(course.trainerId, e)}>{course.trainer}</a>
            {course.trainerNationality && (
              <span
                className={`fi fi-${trainerNationalityFlagCode?.toLowerCase()} flag ml-2`}
              />
            )}
          </p>
        ) : (<><span className="h-125"></span></>)}

        <h6
          title={`${course.title || course.courseName}`}
          className="keduall-typography heading-h6-semibold-600 mt-2 two-line-text"
        >
          {`${course.title || course.courseName}`}
        </h6>
        {!course.isFree && !course.isBought && course.discountPercent > 0 && (
          <h6 className="keduall-typography heading-h6-normal-400 keduall-price keduall-original-price mt-2">
            {course.originalPrice >= course.price ? originalPriceLabel : "-"}
          </h6>
        )}
        <div className="mt-2">
          {course.isFree ? (
            <h6 className="keduall-typography heading-h6-semibold-600 flex align-items-center">
              <span className="text-success-txt">{translations.freeLabel}</span>
            </h6>
          ) : course.isBought ? (
            <h6 className="keduall-typography heading-h6-semibold-600 flex align-items-center">
              <span className="text-success-txt">{translations.boughtLabel}</span>
            </h6>
          ) : (
            <h6 className="keduall-typography heading-h6-semibold-600 flex align-items-center keduall-price keduall-final-price">
            {course.discountPercent > 0 && (
              <span className="keduall-typography text-12-normal-400 keduall-price keduall-discount-percentage mr-2">
                -{course.discountPercent}%
              </span>
            )}
              {priceLabel}
            </h6>
          )}
        </div>
        <p
          title={course.description}
          className="keduall-typography text-14-normal-400 mt-1 three-line-text"
        >
          {course.description}
        </p>
      </div>
      {course.haveDocument && (
        <div className="file mt-auto ml-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M8 1C4.13438 1 1 4.13438 1 8C1 11.8656 4.13438 15 8 15C11.8656 15 15 11.8656 15 8C15 4.13438 11.8656 1 8 1ZM8.5 11.375C8.5 11.4438 8.44375 11.5 8.375 11.5H7.625C7.55625 11.5 7.5 11.4438 7.5 11.375V7.125C7.5 7.05625 7.55625 7 7.625 7H8.375C8.44375 7 8.5 7.05625 8.5 7.125V11.375ZM8 6C7.80374 5.99599 7.61687 5.91522 7.47948 5.775C7.3421 5.63478 7.26515 5.4463 7.26515 5.25C7.26515 5.0537 7.3421 4.86522 7.47948 4.725C7.61687 4.58478 7.80374 4.50401 8 4.5C8.19626 4.50401 8.38313 4.58478 8.52052 4.725C8.6579 4.86522 8.73485 5.0537 8.73485 5.25C8.73485 5.4463 8.6579 5.63478 8.52052 5.775C8.38313 5.91522 8.19626 5.99599 8 6Z"
              fill="black"
            />
          </svg>
          <p>{translations.PDF_AVAILABLE}</p>
        </div>
      )}
    </div>
  );
};

export default VODItem;
